import { type BaseConnection, type CapabilityWizardSpec, localize } from "@ando/ui";
import { useLingui } from "@lingui/react/macro";
import type * as React from "react";

import { ConnectProviderCards } from "./ConnectProviderCards";

/**
 * First-time empty state shown when ``connections.length === 0``.
 *
 * The connectable providers lead, via the shared ``ConnectProviderCards``
 * (same surface used for "Aggiungi un altro account" once connected). The
 * benefits follow underneath in a quiet, typography-first strip — they
 * explain the value without competing with the connect action.
 *
 * Pure presentational: ``onConnect(vendorId)`` comes from the route.
 */
export interface IntegrationsEmptyStateProps {
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  readonly capabilityGranted: boolean;
  readonly onConnect: (vendorId: string) => void;
}

export const IntegrationsEmptyState: React.FC<IntegrationsEmptyStateProps> = ({
  spec,
  capabilityGranted,
  onConnect,
}) => {
  const { t, i18n } = useLingui();
  const title = localize(spec.title, i18n);
  return (
    <section className="flex flex-col items-center gap-5 rounded-xl border border-hairline bg-card/60 px-6 py-8 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-xl bg-ando-orange-soft text-ando-orange">
        {spec.icon}
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="font-semibold text-[14px] text-ink-1">
          {capabilityGranted
            ? t`Collega il tuo primo account ${title}`
            : t`${title} richiede un piano superiore`}
        </h3>
        <p className="max-w-md text-[12.5px] text-ink-3 leading-relaxed">
          {localize(spec.subtitle, i18n)}
        </p>
      </div>

      {/* Primary: the connectable providers (the hero action). */}
      {capabilityGranted ? (
        <div className="w-full max-w-xl pt-1">
          <ConnectProviderCards spec={spec} onConnect={onConnect} />
        </div>
      ) : null}

      {/* Secondary: benefits (quiet, typography-first). */}
      {spec.benefits.length > 0 ? (
        <div className="w-full max-w-xl border-hairline border-t pt-4 text-left">
          <p className="mb-2.5 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.08em]">
            {t`Cosa potrai fare`}
          </p>
          <ul className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {spec.benefits.slice(0, 4).map((b) => {
              const benefitTitle = localize(b.title, i18n);
              const benefitBody = localize(b.body, i18n);
              return (
                <li key={benefitTitle} className="flex items-start gap-2">
                  <span className="mt-0.5 shrink-0 text-ink-4">{b.icon}</span>
                  <div className="min-w-0">
                    <p className="font-medium text-[12px] text-ink-2">{benefitTitle}</p>
                    <p className="text-[11px] text-ink-3 leading-relaxed">{benefitBody}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </section>
  );
};
IntegrationsEmptyState.displayName = "IntegrationsEmptyState";
